// src/components/public/Terminal.js
import { useState, useEffect, useRef } from "react";
import { useLang } from "../../context/LanguageContext";

const THEMES = {
  dark:  { bg: "#0d0d16", bar: "#15151f", userColor: "#3ddc84", hostColor: "#888", pathColor: "#22d3ee", defaultOut: "#8b7bff" },
  light: { bg: "#f5f5f0", bar: "#e8e8e2", userColor: "#1a7a2e", hostColor: "#666", pathColor: "#1a56b0", defaultOut: "#7a5c00" },
  retro: { bg: "#0a0a00", bar: "#1a1a00", userColor: "#aaff00", hostColor: "#667700", pathColor: "#aaff00", defaultOut: "#aaff00" },
  nord:  { bg: "#2e3440", bar: "#3b4252", userColor: "#a3be8c", hostColor: "#81a1c1", pathColor: "#88c0d0", defaultOut: "#ebcb8b" },
};

export default function Terminal({ settings }) {
  const { lang } = useLang();

  const lines = settings
    ? (lang === "en" ? settings.terminal_lines_en : settings.terminal_lines_tr) || []
    : [
        { cmd: "whoami", out: "dursun_inel" },
        { cmd: "cat role.txt", out: "Frontend Manager & Developer" },
        { cmd: "ls skills/", out: "react/   js/   css/   node/" },
        { cmd: "echo $EXPERIENCE", out: lang === "en" ? "5+ years, 17 live projects" : "5+ yıl, 17 canlı proje" },
      ];

  const theme = THEMES[settings?.terminal_theme || "dark"] || THEMES.dark;
  const outColor = settings?.terminal_output_color || theme.defaultOut;
  const typingSpeed = Math.max(20, Math.min(150, Number(settings?.terminal_typing_speed) || 60));
  const titleText = settings?.terminal_title || "terminal — dursun@portfolio";
  const username = settings?.terminal_username || "dursun";
  const hostname = settings?.terminal_hostname || "portfolio";

  const [visible, setVisible] = useState([]);
  const [typed, setTyped] = useState("");
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [phase, setPhase] = useState("typing");
  const contentRef = useRef(null);

  useEffect(() => {
    setVisible([]); setTyped(""); setLineIdx(0); setCharIdx(0); setPhase("typing");
  }, [lang, JSON.stringify(lines)]);

  useEffect(() => {
    if (!lines.length) return;
    if (lineIdx >= lines.length) return;

    const line = lines[lineIdx];
    if (!line) return;

    if (phase === "typing" && line.cmd) {
      if (charIdx < line.cmd.length) {
        const jitter = typingSpeed * 0.4;
        const delay = typingSpeed - jitter + Math.random() * jitter * 2;
        const t = setTimeout(() => { setTyped(line.cmd.slice(0, charIdx + 1)); setCharIdx(c => c + 1); }, delay);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => {
          setVisible(v => [...v, { ...line, doneTyping: true }]);
          setTyped("");
          setCharIdx(0);
          setLineIdx(i => i + 1);
          setPhase("typing");
        }, 200 + Math.random() * 200);
        return () => clearTimeout(t);
      }
    }
  }, [lineIdx, charIdx, phase, lines, typingSpeed]);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = contentRef.current.scrollHeight;
  }, [visible, typed]);

  const Prompt = ({ cmd, typing }) => (
    <div style={{ marginBottom: 1 }}>
      <span style={{ color: theme.userColor }}>{username}</span>
      <span style={{ color: theme.hostColor }}>@{hostname}</span>
      <span style={{ color: theme.hostColor }}>:</span>
      <span style={{ color: theme.pathColor }}>~</span>
      <span style={{ color: theme.hostColor }}>$ </span>
      <span style={{ color: "#f0ede6" }}>{typing ? typed : cmd}</span>
      {typing && <span style={{ animation: "blink 0.8s step-end infinite", color: outColor }}>▋</span>}
    </div>
  );

  return (
    <div style={{ background: theme.bg, border: "1px solid var(--border-strong)", borderRadius: "var(--radius-lg)", fontFamily: "'DM Mono', monospace", fontSize: 12, lineHeight: 1.8, overflow: "hidden", boxShadow: "0 30px 60px -24px rgba(124,108,255,0.4), 0 0 0 1px rgba(255,255,255,0.04)" }}>
      {/* Title bar */}
      <div style={{ background: theme.bar, padding: "10px 16px", borderBottom: "1px solid #2a2a2a", display: "flex", alignItems: "center", gap: 6 }}>
        {["#ff5f57", "#febc2e", "#28c840"].map(c => (
          <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
        ))}
        <span style={{ marginLeft: 8, color: "#666", fontSize: 10, letterSpacing: "0.1em" }}>{titleText}</span>
      </div>
      {/* Content */}
      <div ref={contentRef} style={{ padding: "16px 20px", minHeight: 200, maxHeight: 280, overflowY: "auto" }}>
        {visible.map((line, i) => (
          <div key={i}>
            {line.cmd && <Prompt cmd={line.cmd} typing={false} />}
            {line.out && <div style={{ color: outColor, paddingLeft: 0, marginBottom: 4, wordBreak: "break-all" }}>{line.out}</div>}
          </div>
        ))}
        {lineIdx < lines.length && lines[lineIdx]?.cmd && (
          <Prompt cmd="" typing={true} />
        )}
        {lineIdx >= lines.length && lines.length > 0 && (
          <div>
            <span style={{ color: theme.userColor }}>{username}</span>
            <span style={{ color: theme.hostColor }}>@{hostname}:~$ </span>
            <span style={{ animation: "blink 1s step-end infinite", color: outColor }}>▋</span>
          </div>
        )}
      </div>
      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
    </div>
  );
}
